import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Dumbbell, Menu, Search, User } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';
import ClimberIcon from './icons/climber-icon';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import NavbarMenu from './navbar-menu';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumbContext } from '../contexts/breadcrumb-context';
import { useNotificationContext } from '../contexts/notification-context';
import { cn } from '../lib/utils';

type DesktopNavItem = {
  to: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  isActive?: (pathname: string) => boolean;
};

// Search only covers the climb feed, so the icon is misleading on other pages
const SEARCH_PATHS = ['/dashboard', '/climbs'];

export default function Navbar() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { breadcrumb, setNavbarSlot } = useBreadcrumbContext();
  // Stable, or the slots would be handed back and forth on every render.
  // Optional-called: a test that mocks the context may leave it out.
  const actionsSlotRef = useCallback((node: HTMLDivElement | null) => setNavbarSlot?.('actions', node), [setNavbarSlot]);
  const stripSlotRef = useCallback((node: HTMLDivElement | null) => setNavbarSlot?.('strip', node), [setNavbarSlot]);
  const { unreadCount } = useNotificationContext();

  // The header's height, for what sticks under it: 65px, more while a page
  // draws into the strip. Read as var(--navbar-height, 65px).
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === 'undefined') return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() =>
      root.style.setProperty('--navbar-height', `${header.getBoundingClientRect().height}px`)
    );
    observer.observe(header);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--navbar-height');
    };
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const closeMenu = () => setIsMenuOpen(false);
  const toggleMenu = () => setIsMenuOpen((prev) => !prev);

  // Close on click outside the trigger + panel
  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const onTrigger = menuButtonRef.current?.contains(target);
      const onPanel = menuRef.current?.contains(target);
      if (!onTrigger && !onPanel) closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  // Close when route changes
  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const goToSearch = () => navigate('/search');
  const goToNotifications = () => navigate('/notifications');

  const profilePath = user?.slug ? `/users/${user.slug}` : '/profile';
  const logoTo = isAuthenticated ? '/dashboard' : '/';
  const showSearch = SEARCH_PATHS.includes(location.pathname.replace(/\/+$/, '') || '/');

  const desktopNavItems: DesktopNavItem[] = isAuthenticated
    ? [
        { to: '/climbs', icon: ClimberIcon, label: t('navbar.climbs') },
        { to: '/training-diary', icon: Dumbbell, label: t('navbar.trainingDiary') },
        {
          to: profilePath,
          icon: User,
          label: t('navbar.profile'),
          isActive: (pathname) => pathname.startsWith('/users/'),
        },
      ]
    : [];

  return (
    <header ref={headerRef} className="border-b bg-background sticky top-0 z-40">
      <div className="container relative flex h-16 items-center px-4 sm:px-6 gap-4">
        {/* Logo / breadcrumb. Allowed to shrink (min-w-0) so a long trail
            truncates its labels instead of pushing the actions off screen. */}
        <div className="flex min-w-0 items-center">
          {breadcrumb ? (
            <div className="flex min-w-0 items-center gap-1.5">
              {!breadcrumb.hideHomeLogo && (
                breadcrumb.onHomeLogo ? (
                  <button type="button" onClick={breadcrumb.onHomeLogo} className="flex shrink-0 items-center space-x-2" aria-label="DivePeaks">
                    <img src="/icons/icon-192x192.svg" alt="DivePeaks logo" className="h-8 w-8 animate-in fade-in duration-300" />
                  </button>
                ) : (
                  <Link to={logoTo} className="flex shrink-0 items-center space-x-2">
                    <img src="/icons/icon-192x192.svg" alt="DivePeaks logo" className="h-8 w-8 animate-in fade-in duration-300" />
                  </Link>
                )
              )}
              {breadcrumb.loading ? (
                <Skeleton className="h-5 w-32" />
              ) : (
                breadcrumb.segments.map((seg, i) => {
                  const isOnlySegment = breadcrumb.segments.length === 1;
                  const isLastSegment = i === breadcrumb.segments.length - 1;
                  const widthClass = isOnlySegment
                    ? 'max-w-[14rem] sm:max-w-[22rem]'
                    : isLastSegment
                    ? 'max-w-[15rem] sm:max-w-[20rem]'
                    : 'max-w-[6rem] sm:max-w-[12rem]';
                  const labelInitials = seg.label
                    .split(/\s+/)
                    .filter(Boolean)
                    .slice(0, 2)
                    .map((w) => w.charAt(0).toUpperCase())
                    .join('');
                  const renderLabel = (
                    <span
                      className={`text-sm truncate ${widthClass} ${
                        seg.muted ? 'text-muted-foreground' : 'font-semibold'
                      } ${seg.hideOnNarrow ? 'hidden sm:inline' : ''}`}
                    >
                      {seg.label}
                    </span>
                  );
                  return (
                    <Fragment key={i}>
                      {i > 0 && (
                        <span
                          className={`shrink-0 text-muted-foreground text-xs select-none ${
                            seg.hideOnNarrow ? 'hidden sm:inline' : ''
                          }`}
                        >
                          ›
                        </span>
                      )}
                      {seg.leadingIcon !== undefined ? (
                        seg.onClick ? (
                          <button
                            onClick={seg.onClick}
                            aria-label={seg.label}
                            className={`flex items-center gap-1.5 hover:opacity-80 transition-opacity ${
                              seg.hideOnNarrow ? 'hidden sm:inline-flex' : ''
                            }`}
                          >
                            {seg.leadingIcon}
                            <span className={`text-sm font-semibold truncate ${widthClass}`}>
                              {seg.label}
                            </span>
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            {seg.leadingIcon}
                            <span className={`text-sm font-semibold truncate ${widthClass}`}>
                              {seg.label}
                            </span>
                          </div>
                        )
                      ) : seg.avatarUrl !== undefined ? (
                        seg.onClick ? (
                          <button
                            onClick={seg.onClick}
                            aria-label={seg.label}
                            className={`flex shrink-0 items-center gap-1.5 hover:opacity-80 transition-opacity ${
                              seg.hideOnNarrow ? 'hidden sm:inline-flex' : ''
                            }`}
                          >
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={seg.avatarUrl} alt={seg.label} />
                              <AvatarFallback className="text-xs font-bold">
                                {labelInitials || <ClimberIcon className="h-4 w-4" />}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={`text-sm font-semibold truncate hidden sm:inline ${widthClass}`}
                            >
                              {seg.label}
                            </span>
                          </button>
                        ) : (
                          <div className="flex shrink-0 items-center gap-1.5">
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={seg.avatarUrl} alt={seg.label} />
                              <AvatarFallback className="text-xs font-bold">
                                {labelInitials || <ClimberIcon className="h-4 w-4" />}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={`text-sm font-semibold truncate hidden sm:inline ${widthClass}`}
                            >
                              {seg.label}
                            </span>
                          </div>
                        )
                      ) : seg.onClick && seg.trailingIcon !== undefined ? (
                        <button
                          onClick={seg.onClick}
                          className={`flex min-w-0 items-center gap-1 text-sm font-semibold hover:text-primary transition-colors ${
                            seg.hideOnNarrow ? 'hidden sm:inline-flex' : ''
                          }`}
                        >
                          <span className={`truncate ${widthClass}`}>{seg.label}</span>
                          {seg.trailingIcon}
                        </button>
                      ) : seg.onClick ? (
                        <button
                          onClick={seg.onClick}
                          className={`min-w-0 text-sm font-semibold hover:text-primary transition-colors truncate ${widthClass} ${
                            seg.hideOnNarrow ? 'hidden sm:inline-flex' : ''
                          }`}
                        >
                          {seg.label}
                        </button>
                      ) : (
                        renderLabel
                      )}
                    </Fragment>
                  );
                })
              )}
            </div>
          ) : (
            <Link to={logoTo} className="flex items-center space-x-2">
              <img src="/icons/icon-192x192.svg" alt="DivePeaks logo" className="h-8 w-8" />
              <span className="font-semibold hidden sm:inline">{t('app.name')}</span>
            </Link>
          )}
        </div>

        {/* Desktop primary nav (logged-in only) */}
        {isAuthenticated && (
          <nav className="hidden md:flex shrink-0 items-center gap-1 ml-4" aria-label={t('bottomNav.label')}>
            {desktopNavItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={!item.isActive}
                  className={({ isActive }) => {
                    const active = item.isActive ? item.isActive(location.pathname) : isActive;
                    return cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium',
                      'transition-colors',
                      active
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/60',
                    );
                  }}
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        )}

        {/* Right-side actions */}
        <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2">
          {/* What a page puts at the head of the buttons (NavbarPortal): a
              round's clock. Takes no room while empty. */}
          <div ref={actionsSlotRef} className="flex items-center empty:hidden" />
          {showSearch && (
            <Button
              variant="ghost"
              size="icon"
              onClick={goToSearch}
              aria-label={t('navbar.search')}
              className="text-muted-foreground hover:text-foreground"
            >
              <Search className="h-5 w-5" />
            </Button>
          )}

          {isAuthenticated ? (
            user?.feature_flags?.new_homepage && (
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNotifications}
                aria-label="Notifications"
                className="relative text-muted-foreground hover:text-foreground"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {unreadCount > 99 ? '99' : unreadCount}
                  </span>
                )}
              </Button>
            )
          ) : (
            <Link to="/sign-in">
              <Button variant="default" size="sm">
                {t('navbar.signIn')}
              </Button>
            </Link>
          )}

          {/* Menu trigger: avatar (logged-in desktop) / hamburger (everywhere else) */}
          <button
            ref={menuButtonRef}
            type="button"
            onClick={toggleMenu}
            aria-label={t('navbar.menu')}
            aria-expanded={isMenuOpen}
            aria-haspopup="menu"
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isAuthenticated ? (
              <>
                <span className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted">
                  <Menu className="h-5 w-5" />
                </span>
                <Avatar className="hidden md:inline-flex h-8 w-8 cursor-pointer">
                  <AvatarImage
                    src={user?.avatar_small_url || user?.avatar_url}
                    alt={user?.full_name || user?.email || 'User'}
                  />
                  <AvatarFallback>
                    <User className="h-4 w-4" />
                  </AvatarFallback>
                </Avatar>
              </>
            ) : (
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted">
                <Menu className="h-5 w-5" />
              </span>
            )}
          </button>
        </div>

        {/* Menu panel: anchored to the sticky header so it always sits below the navbar */}
        {isMenuOpen && (
          <div className="absolute right-4 sm:right-6 top-full mt-2 w-[min(20rem,calc(100vw-2rem))] z-50">
            <NavbarMenu ref={menuRef} onItemSelect={closeMenu} />
          </div>
        )}
      </div>
      {/* A row under the bar a page may draw into (NavbarPortal): a round's
          clock and its progress line. Sticky with the rest, no room while empty. */}
      <div ref={stripSlotRef} className="empty:hidden" />
    </header>
  );
}
